import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import clientApi from '../services/clientApi';

const ClientAuthContext = createContext(null);

export const useClientAuth = () => {
  const context = useContext(ClientAuthContext);
  if (!context) {
    throw new Error('useClientAuth must be used within a ClientAuthProvider');
  }
  return context;
};

export const ClientAuthProvider = ({ children }) => {
  const [clientUser, setClientUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkClientAuth = useCallback(async () => {
    const token = localStorage.getItem('client_token');
    if (token) {
      try {
        clientApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await clientApi.get('/client/auth/me');
        const userData = response.data.user || response.data;
        if (userData && userData.id) {
          setClientUser(userData);
        } else {
          throw new Error('Invalid user data');
        }
      } catch (err) {
        console.error('Client auth check failed:', err);
        localStorage.removeItem('client_token');
        delete clientApi.defaults.headers.common['Authorization'];
        setClientUser(null);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkClientAuth();
  }, [checkClientAuth]);

  const processAuthResponse = (data) => {
    const { token, user: userData } = data;
    if (!token) throw new Error('No token received');

    localStorage.setItem('client_token', token);
    clientApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    // Build user object - handle both old (profile) and new (user+profile) format
    const userObj = userData || data.profile;
    if (userObj) {
      setClientUser(userObj);
    }
    return { success: true };
  };

  const clientLogin = async (email, password) => {
    try {
      setError(null);
      const response = await clientApi.post('/client/auth/login', { email, password });
      return processAuthResponse(response.data);
    } catch (err) {
      console.error('Client login error:', err);
      const message = err.response?.data?.message || err.response?.data?.error || err.message || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const clientOTPLogin = async (phone, otp) => {
    try {
      setError(null);
      const response = await clientApi.post('/client/auth/verify-otp', { phone, otp });
      return processAuthResponse(response.data);
    } catch (err) {
      console.error('Client OTP login error:', err);
      const message = err.response?.data?.error || err.message || 'OTP login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const clientRegister = async (data) => {
    try {
      setError(null);
      const response = await clientApi.post('/client/auth/register', data);
      return processAuthResponse(response.data);
    } catch (err) {
      const message = err.response?.data?.error || err.response?.data?.message || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const clientGoogleLogin = async (accessToken) => {
    try {
      setError(null);
      const response = await clientApi.post('/client/auth/google-login', { access_token: accessToken });
      return processAuthResponse(response.data);
    } catch (err) {
      console.error('Client Google login error:', err);
      const message = err.response?.data?.error || err.message || 'Google login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const clientAppleLogin = async (id_token, user) => {
    try {
      setError(null);
      const response = await clientApi.post('/client/auth/apple-login', { id_token, user });
      return processAuthResponse(response.data);
    } catch (err) {
      console.error('Client Apple login error:', err);
      const message = err.response?.data?.error || err.message || 'Apple login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const clientLogout = () => {
    localStorage.removeItem('client_token');
    delete clientApi.defaults.headers.common['Authorization'];
    setClientUser(null);
  };

  const updateClientProfile = (data) => {
    setClientUser((prev) => ({ ...prev, ...data }));
  };

  const value = {
    clientUser,
    loading,
    error,
    clientLogin,
    clientOTPLogin,
    clientRegister,
    clientGoogleLogin,
    clientAppleLogin,
    clientLogout,
    updateClientProfile,
    isClientAuthenticated: !!clientUser,
  };

  return (
    <ClientAuthContext.Provider value={value}>
      {children}
    </ClientAuthContext.Provider>
  );
};

export default ClientAuthContext;
